import { useEffect, useMemo, useState } from 'react';

import { Divider, Modal } from '@mui/material';

import { CfsApi } from '@/api';
import { ModalContent } from '@/components/Modals/SideModal/styles';
import { Button } from '@/components/TailwindComponents';
import { Separator } from '@/components/TailwindComponents/base/Separator/Separator';
import { LocalStorageItems } from '@/constants';
import { withLoader, WithLoaderProps } from '@/hocs';
import { CFSEvent } from '@/models';
import StepperItem from '@/pages/CFS/CFSPrintModal/StepperItem';
import {
  cfsReportSections,
  cfsTopSectionFields,
} from '@/pages/CFS/reports/cfsReport/template';
import type { CFSReportContext } from '@/pages/CFS/reports/cfsReport/types';
import { useAgencyStore, useUserStore } from '@/store';

import CFSWordSearchRedaction from './components/CFSWordSearchRedaction';
import PDFPreviewPage from './components/PDFPreviewPage';
import RedactionsPage from './components/RedactionsPage';
import { useCFSRedactions } from './hooks/useCFSRedactions';

interface ICFSPrintModalProps extends WithLoaderProps {
  openPrintModal: boolean;
  setOpenPrintModal: (shouldOpen: boolean) => void;
  cfsId?: string;
}

const CFSPrintModal = ({
  cfsId,
  openPrintModal,
  setOpenPrintModal,
}: ICFSPrintModalProps) => {
  if (!cfsId || !openPrintModal) {
    return <></>;
  }
  const { user } = useUserStore();
  const { currentAgency, fetchAgency, agencies } = useAgencyStore();

  const [activePrintPage, setActivePrintPage] = useState<0 | 1>(0);
  const [cfsData, setCfsData] = useState<CFSReportContext | undefined>(
    undefined,
  );

  const [, setGeneratedPDFBlob] = useState<Blob | null>(null);
  const [generatedPDFUrl, setGeneratedPDFUrl] = useState<string | null>(null);

  const [activeSearchTerm, setActiveSearchTerm] = useState('');
  const [currentMatchKey, setCurrentMatchKey] = useState<string | null>(null);

  const {
    redactions,
    toggleFieldRedaction,
    addOccurrenceRedactions,
    removeOccurrenceRedactions,
    resetAll,
  } = useCFSRedactions(cfsData);

  const allSections = useMemo(
    () => [cfsTopSectionFields, ...cfsReportSections],
    [],
  );

  const fetchCurrentCfs = async () => {
    if (!cfsId) return;
    const fetchedCfsData = await CfsApi.getOne(cfsId);

    // 2. Load agency
    let agencyData = currentAgency;
    if (!agencyData?._id) {
      const agencyId = localStorage.getItem(LocalStorageItems.CurrentAgencyId);
      if (agencyId) {
        agencyData = (await fetchAgency(agencyId)) ?? null;
      }
    }

    // 3. Fetch sub-data in parallel (allSettled so one failure doesn't block print)
    const [
      namesResult,
      vehiclesResult,
      narrativesResult,
      closeReasonsResult,
      timelineResult,
    ] = await Promise.allSettled([
      CfsApi.getCfsMasterNames(cfsId),
      CfsApi.getCfsMasterVehicles(cfsId),
      CfsApi.getLinkedNarratives(cfsId, {}),
      CfsApi.getCloseReasons(),
      CfsApi.getCfsHistory(cfsId, {
        limit: 1000,
        sort: JSON.stringify({ createdAt: -1 }),
        filter: JSON.stringify({
          event: {
            $in: [
              CFSEvent.CREATED,
              CFSEvent.UNIT_SHIFT_ASSIGNED,
              CFSEvent.UNIT_SHIFT_IS_EN_ROUTE,
              CFSEvent.UNIT_SHIFT_IS_ON_SCENE,
              CFSEvent.CLOSED,
            ],
          },
        }),
      }),
    ]);

    const cfsNames =
      namesResult.status === 'fulfilled' ? namesResult.value.data || [] : [];
    const cfsVehicles =
      vehiclesResult.status === 'fulfilled'
        ? vehiclesResult.value.data || []
        : [];
    const narratives =
      narrativesResult.status === 'fulfilled'
        ? narrativesResult.value.data || []
        : [];
    const cfsCloseReasons =
      closeReasonsResult.status === 'fulfilled'
        ? closeReasonsResult.value.data || []
        : [];
    const cfsTimelineLogs =
      timelineResult.status === 'fulfilled'
        ? timelineResult.value.data?.results || []
        : [];

    setCfsData({
      cfs: { ...fetchedCfsData.data },
      agency: agencyData as any,
      agencies,
      user,
      cfsNames,
      cfsVehicles,
      narratives,
      cfsCloseReasons,
      cfsTimelineLogs,
      printedAt: new Date(),
    });
  };

  useEffect(() => {
    fetchCurrentCfs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfsId, openPrintModal]);

  const handlePrint = () => {
    if (!generatedPDFUrl) return;
    const newWindow = window.open('', '_blank');
    if (!newWindow) return;
    const iframe = document.createElement('iframe');
    iframe.src = generatedPDFUrl;
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = 'none';
    iframe.onload = () => {
      setTimeout(() => {
        iframe.focus();
        iframe.contentWindow?.print();
      }, 1000);
    };
    newWindow.document.body.appendChild(iframe);
  };

  const handleClose = () => {
    setActivePrintPage(0);
    resetAll();
    setActiveSearchTerm('');
    setCurrentMatchKey(null);
    if (generatedPDFUrl) {
      URL.revokeObjectURL(generatedPDFUrl);
    }
    setGeneratedPDFBlob(null);
    setGeneratedPDFUrl(null);
    setCfsData(undefined);
    setOpenPrintModal(false);
  };

  const handlePDFGenerated = (blob: Blob | null, url: string | null) => {
    setGeneratedPDFBlob(blob);
    setGeneratedPDFUrl(url);
  };

  return (
    <>
      <Modal open={openPrintModal}>
        <div
          style={{
            display: 'flex',
            height: '95vh',
            marginTop: '20px',
            justifyContent: 'space-around',
          }}
        >
          <ModalContent
            width="70%"
            minWidth="210mm"
            borderRadius="8px"
            overflow="auto"
          >
            <nav
              style={{
                padding: '20px 35px',
                borderRadius: '8px 8px 0 0',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                minHeight: '85px',
              }}
            >
              <ol style={{ display: 'flex', alignItems: 'center' }}>
                <StepperItem
                  index={0}
                  active={activePrintPage === 0}
                  done={activePrintPage > 0}
                  onClick={() => setActivePrintPage(0)}
                >
                  Redactions
                </StepperItem>
                <li aria-hidden="true" className="flex items-center mr-4">
                  <Separator className="w-16 hidden lg:block" />
                </li>
                <StepperItem
                  index={1}
                  active={activePrintPage === 1}
                  done={false}
                  onClick={() => setActivePrintPage(1)}
                >
                  PDF Preview
                </StepperItem>
              </ol>

              {activePrintPage === 0 && cfsData && (
                <CFSWordSearchRedaction
                  ctx={cfsData}
                  sections={allSections}
                  redactions={redactions}
                  onAddOccurrences={addOccurrenceRedactions}
                  onRemoveOccurrences={removeOccurrenceRedactions}
                  onSearchUpdate={setActiveSearchTerm}
                  onCurrentMatchUpdate={setCurrentMatchKey}
                />
              )}
            </nav>
            <Divider sx={{ margin: '0 15px' }} />
            {cfsData && (
              <>
                <div style={{ flex: 1, overflow: 'auto' }}>
                  {activePrintPage === 0 ? (
                    <RedactionsPage
                      ctx={cfsData}
                      redactions={redactions}
                      toggleFieldRedaction={toggleFieldRedaction}
                      activeSearchTerm={activeSearchTerm}
                      currentMatchKey={currentMatchKey}
                    />
                  ) : (
                    <PDFPreviewPage
                      ctx={cfsData}
                      redactions={redactions}
                      onPDFGenerated={handlePDFGenerated}
                    />
                  )}
                </div>

                <div className="flex flex-row justify-between items-center w-full gap-2.5 px-4 py-4 border-t border-gray-200 dark:border-[#192932] bg-white dark:bg-[#17252D] text-gray-900 dark:text-gray-100 rounded-b-md">
                  <Button
                    variant="secondary"
                    size="default"
                    onClick={() => {
                      if (activePrintPage === 0) {
                        handleClose();
                      } else {
                        setActivePrintPage(0);
                      }
                    }}
                  >
                    {activePrintPage === 0 ? 'Cancel' : 'Back'}
                  </Button>
                  <Button
                    variant="destructive"
                    size="default"
                    onClick={() => {
                      if (activePrintPage === 1) {
                        handlePrint();
                      } else {
                        setActivePrintPage(1);
                      }
                    }}
                    disabled={activePrintPage === 1 && !generatedPDFUrl}
                  >
                    {activePrintPage === 1 ? 'Print' : 'Next'}
                  </Button>
                </div>
              </>
            )}
          </ModalContent>
        </div>
      </Modal>
    </>
  );
};

export default withLoader(CFSPrintModal);
